import React from "react";
import { Link } from "react-router-dom";
import { Repeat2 } from "lucide-react";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import PostCarousel from "@/components/PostCarousel";
import LinkPreviewCard from "@/components/LinkPreviewCard";
import { linkify } from "@/components/ExpandableText";
import { MusicTag, LocationTag } from "@/components/MusicLocation";

/** Quoted original post inside a repost: credit + text + link preview + photos/video. */
export default function RepostOriginal({ p, testidPrefix = "repost" }) {
  const gallery = Array.isArray(p.image_urls) && p.image_urls.length ? p.image_urls : (p.image_url ? [p.image_url] : []);
  const hasAny = p.original_content || gallery.length || p.video_url || p.link_preview;
  if (!hasAny) return null;
  return (
    <div className="mb-3 rounded-xl border border-dashed border-border bg-muted/40 p-3" data-testid={`${testidPrefix}-original-${p.post_id}`}>
      <div className="flex items-center gap-2 mb-2">
        <Link to={`/user/${p.original_user_id}`} className="hover:opacity-80">
          <Avatar className="w-7 h-7">
            <AvatarImage src={p.original_user_picture} />
            <AvatarFallback>{p.original_user_name?.[0]}</AvatarFallback>
          </Avatar>
        </Link>
        <div className="leading-tight">
          <Link to={`/user/${p.original_user_id}`} className="text-sm font-semibold hover:underline" data-testid={`${testidPrefix}-original-author-${p.post_id}`}>{p.original_user_name || "Usuário"}</Link>
          {p.original_created_at && <div className="text-[10px] text-muted-foreground">{new Date(p.original_created_at).toLocaleString()}</div>}
        </div>
      </div>
      {p.original_content && <p className="text-sm whitespace-pre-wrap opacity-90 break-words mb-2" data-testid={`${testidPrefix}-original-text-${p.post_id}`}>{linkify(p.original_content)}</p>}
      {p.link_preview && <LinkPreviewCard preview={p.link_preview} testid={`${testidPrefix}-original-link-${p.post_id}`} />}
      {(p.music || p.location) && (
        <div className="flex flex-wrap items-center gap-2 mb-2">
          <MusicTag music={p.music} testid={`${testidPrefix}-original-music-${p.post_id}`} />
          <LocationTag location={p.location} testid={`${testidPrefix}-original-location-${p.post_id}`} />
        </div>
      )}
      {p.album_title && gallery.length >= 3 && <div className="text-xs font-semibold mb-1">📚 {p.album_title} · {gallery.length} fotos</div>}
      {gallery.length > 0 && <PostCarousel urls={gallery} testidPrefix={`${testidPrefix}-original-carousel-${p.post_id}`} />}
      {p.video_url && (
        <video src={p.video_url} poster={p.cover_url || undefined} controls playsInline preload="metadata" className="w-full max-h-[60vh] rounded-xl bg-black mt-2" data-testid={`${testidPrefix}-original-video-${p.post_id}`} />
      )}
      <div className="text-[10px] text-muted-foreground mt-2 flex items-center gap-1" data-testid={`${testidPrefix}-credit-${p.post_id}`}>
        <Repeat2 className="w-3 h-3" /> Publicação original de <Link to={`/user/${p.original_user_id}`} className="font-semibold hover:underline">{p.original_user_name || "Usuário"}</Link>
        {p.original_post_id && <> · <Link to={`/post/${p.original_post_id}`} className="hover:underline">ver original</Link></>}
      </div>
    </div>
  );
}
